import { Box, Checkbox, useTheme, alpha } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";

interface TaskCheckboxProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  ariaLabel: string;
  disabled?: boolean;
}

// Custom-styled MUI Checkbox: 18×18 / 5px radius / 1.5px border per spec §6.6.
// Disabled muting uses SOLID colors (alpha-blended grey for unchecked, the
// theme's success.dark for checked) — NOT alpha overlays of the active color.
// Alpha overlays of green-on-white visually shift toward cyan/aqua. Solid
// shade variants preserve hue identity. See memory:
// feedback_visual_contrast_thresholds.md.

function UncheckedIcon({ disabled }: { disabled?: boolean }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        width: 18,
        height: 18,
        borderRadius: "5px",
        border: "1.5px solid",
        borderColor: alpha(theme.palette.text.primary, disabled ? 0.4 : 0.35),
        bgcolor: disabled
          ? alpha(theme.palette.text.primary, 0.12)
          : "transparent",
      }}
    />
  );
}

function CheckedIcon({ disabled }: { disabled?: boolean }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        width: 18,
        height: 18,
        borderRadius: "5px",
        bgcolor: disabled
          ? theme.palette.success.dark
          : theme.palette.success.main,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <CheckIcon sx={{ fontSize: 14, color: "common.white", strokeWidth: 3 }} />
    </Box>
  );
}

export default function TaskCheckbox({
  checked,
  onChange,
  ariaLabel,
  disabled = false,
}: TaskCheckboxProps) {
  const theme = useTheme();

  return (
    <Checkbox
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
      inputProps={{ "aria-label": ariaLabel }}
      icon={<UncheckedIcon disabled={disabled} />}
      checkedIcon={<CheckedIcon disabled={disabled} />}
      disableRipple
      sx={{
        // Visual stays 18×18 (spec §6.6), but the TAP TARGET must be ≥44×44
        // (WCAG 2.5.5). Expand the clickable root to 44×44 and pull it back with
        // a negative margin so the 18×18 icon stays exactly in place and the grid
        // footprint is unchanged — the extra hit area lands in the card padding /
        // grid gap, not on other controls.
        p: 0,
        width: 44,
        height: 44,
        m: "-13px",
        flexShrink: 0,
        "&.Mui-focusVisible > span:first-of-type": {
          outline: `2px solid ${theme.palette.secondary.main}`,
          outlineOffset: 2,
          borderRadius: "5px",
        },
      }}
    />
  );
}
